import { useCallback, useEffect, useState } from "react";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, ChevronDown, Clock, AlertTriangle, ShieldX, Loader2, BookOpen } from "lucide-react";
import { MarkAttemptDialog } from "@/components/t4r/MarkAttemptDialog";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

const Kpi = ({ label, value, tone = "slate", testId }) => (
  <div className="rounded-lg border border-border bg-white p-3" data-testid={testId}>
    <p className={`font-heading text-2xl font-bold ${tone === "rose" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : tone === "green" ? "text-green-700" : "text-slate-900"}`}>{value ?? "—"}</p>
    <p className="text-[11px] text-muted-foreground">{label}</p>
  </div>
);

const Bar = ({ pct }) => (
  <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-green-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
);

const LessonRow = ({ l }) => (
  <div className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`progress-lesson-${l.id}`}>
    {l.passed ? <CheckCircle2 size={14} className="text-green-600 shrink-0" /> : <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-slate-300" />}
    <span className="flex-1 truncate">{l.title}</span>
    {l.awaiting_marking && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-1"><Clock size={9} /> marking</Badge>}
    {l.ai_flagged && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 gap-1"><AlertTriangle size={9} /> AI</Badge>}
    <span className="w-20 text-right text-muted-foreground">{l.has_quiz ? `${l.attempts} attempt${l.attempts === 1 ? "" : "s"}` : l.video_quiz ? `${l.video_quiz.attempts} watch${l.video_quiz.attempts === 1 ? "" : "es"}` : "No quiz"}</span>
    <span className="w-12 text-right font-medium" data-testid={`progress-lesson-score-${l.id}`}>{l.best_score != null ? `${l.best_score}%` : l.video_quiz ? `${l.video_quiz.best}/${l.video_quiz.total}${l.video_quiz.pass_mark ? (l.video_quiz.best >= l.video_quiz.pass_mark ? " ✓" : ` (needs ${l.video_quiz.pass_mark})`) : ""}` : "—"}</span>
  </div>
);

const CourseBlock = ({ c }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-border bg-white" data-testid={`progress-course-${c.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-3 text-left space-y-2" data-testid={`progress-course-toggle-${c.id}`}>
        <div className="flex items-center gap-2">
          <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="font-medium flex-1 truncate">{c.title}</span>
          {!c.is_published && <Badge variant="outline" className="text-[10px]">draft</Badge>}
          <span className="text-xs text-muted-foreground">{c.passed_lessons}/{c.lesson_count} lessons · {c.progress_pct}%</span>
        </div>
        <Bar pct={c.progress_pct} />
      </button>
      {open && <div className="border-t border-border divide-y divide-slate-50">{c.lessons.map((l) => <LessonRow key={l.id} l={l} />)}{c.lessons.length === 0 && <p className="p-3 text-xs italic text-muted-foreground">No lessons yet.</p>}</div>}
    </div>
  );
};

const fmtTime = (d) => new Date(d).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const AttemptTag = ({ a }) => a.is_current
  ? <Badge className="text-[10px] bg-[#0b2545] text-white border-[#0b2545] hover:bg-[#0b2545]" data-testid={`progress-attempt-current-${a.id}`}>Current</Badge>
  : <span className="text-[10px] italic text-slate-400" data-testid={`progress-attempt-earlier-${a.id}`}>Earlier attempt</span>;

const AttemptRow = ({ a, onOpen }) => {
  const written = (a.answers || []).some((x) => x.kind === "written");
  return (
    <div className={`flex items-center gap-2 px-3 py-2 text-xs transition-opacity ${a.is_current ? "bg-white" : "bg-slate-50/60 opacity-60 hover:opacity-100"}`} data-testid={`progress-attempt-${a.id}`}>
      <span className="flex-1 min-w-0 truncate">
        <span className={a.is_current ? "font-semibold" : ""}>{a.lesson_title || "Lesson quiz"}</span>
        <span className="text-muted-foreground"> · Attempt {a.attempt_no} of {a.attempt_total} · {fmt(a.submitted_at)} · {fmtTime(a.submitted_at)}</span>
      </span>
      <AttemptTag a={a} />
      {a.ai_flags?.breach_confirmed && <Badge variant="outline" className="text-[10px] bg-rose-600 text-white border-rose-600 gap-1"><ShieldX size={9} /> breach</Badge>}
      <Badge variant="outline" className={`text-[10px] ${a.marking_status === "awaiting" ? "bg-amber-50 text-amber-700 border-amber-200" : a.passed ? "bg-green-100 text-green-700 border-green-200" : ""}`}>
        {a.marking_status === "awaiting" ? `${a.score_pct}% provisional` : `${a.score_pct}% ${a.passed ? "pass" : "fail"}`}
      </Badge>
      {written && (a.is_current
        ? <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onOpen(a)} data-testid={`progress-attempt-mark-${a.id}`}>{a.marking_status === "awaiting" ? "Mark" : "Review"}</Button>
        : <Button size="sm" variant="ghost" className="h-6 text-[11px] text-slate-500" onClick={() => onOpen(a)} data-testid={`progress-attempt-view-${a.id}`}>View</Button>)}
    </div>
  );
};

export const LearnerProgressDialog = ({ learnerId, onClose }) => {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(null);
  const load = useCallback(() => { api.get(`/lms/admin/learners/${learnerId}/progress`).then(({ data: d }) => setData(d)).catch(() => setData({ error: true })); }, [learnerId]);
  useEffect(() => { if (learnerId) { setData(null); load(); } }, [learnerId, load]);
  if (!learnerId) return null;
  const s = data?.summary; const l = data?.learner;

  return (
    <Dialog open={!!learnerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-50" data-testid="learner-progress-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{l?.name || "Learner"}{l && <Badge variant="outline" className="text-[10px] capitalize">{l.status === "archived" ? "suspended" : l.status}</Badge>}</DialogTitle>
          <DialogDescription className="text-xs">{l ? `${l.email} · invited ${fmt(l.invited_at)} · activated ${fmt(l.activated_at)} · last sign-in ${fmt(l.last_login_at)}` : "Loading progress…"}</DialogDescription>
        </DialogHeader>
        {!data && <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>}
        {data?.error && <p className="text-sm text-rose-600">Couldn't load progress.</p>}
        {s && (
          <div className="space-y-5">
            <div className="rounded-lg border border-border bg-white p-4 space-y-2" data-testid="progress-overall">
              <div className="flex items-center justify-between text-sm"><span className="font-medium">Overall progress</span><span className="font-heading text-xl font-bold text-green-700">{s.overall_progress_pct}%</span></div>
              <Bar pct={s.overall_progress_pct} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="Quiz attempts" value={s.attempts} testId="progress-kpi-attempts" />
              <Kpi label="Average score" value={s.average_score != null ? `${s.average_score}%` : "—"} tone="green" testId="progress-kpi-average" />
              <Kpi label="Awaiting marking" value={s.awaiting_marking} tone="amber" testId="progress-kpi-awaiting" />
              <Kpi label={`AI flagged · ${s.ai_breaches} confirmed`} value={s.ai_flagged} tone="rose" testId="progress-kpi-flagged" />
            </div>
            <section className="space-y-2">
              <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><BookOpen size={14} /> Courses</h3>
              {data.courses.map((c) => <CourseBlock key={c.id} c={c} />)}
              {data.courses.length === 0 && <p className="text-xs italic text-muted-foreground">Not enrolled on any courses.</p>}
            </section>
            <section className="space-y-2">
              <h3 className="font-heading text-sm font-semibold">Recent attempts</h3>
              <div className="rounded-lg border border-border bg-white divide-y divide-slate-50">
                {data.recent_attempts.map((a) => <AttemptRow key={a.id} a={a} onOpen={setMarking} />)}
                {data.recent_attempts.length === 0 && <p className="p-3 text-xs italic text-muted-foreground">No attempts yet.</p>}
              </div>
              {s.removed_course_attempts > 0 && <p className="text-[11px] text-muted-foreground">{s.removed_course_attempts} attempt(s) are from courses this learner has since been removed from (kept for staff review).</p>}
            </section>
          </div>
        )}
        <MarkAttemptDialog attempt={marking} onClose={() => setMarking(null)} onSaved={() => { setMarking(null); load(); }} />
      </DialogContent>
    </Dialog>
  );
};
